import React, { useEffect, useState, useRef } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { DailyDataPoint, GoalMetric, DailyLoanData, Loan } from '../utils/goalData';
interface DailyBreakdownProps {
  dailyData: DailyDataPoint[];
  daysPassed: number;
  softPullGoal: GoalMetric;
  submittedGoal: GoalMetric;
  dailyLoans: DailyLoanData[];
}
export function DailyBreakdown({
  dailyData,
  daysPassed,
  softPullGoal,
  submittedGoal,
  dailyLoans
}: DailyBreakdownProps) {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0
  });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const dailyIncrements = dailyData.map((d, i) => {
    const prevSP = i > 0 ? dailyData[i - 1].softPullActual : 0;
    const prevSub = i > 0 ? dailyData[i - 1].submittedActual : 0;
    return {
      label: d.label,
      day: d.day,
      softPull: d.day <= daysPassed ? d.softPullActual - prevSP : null,
      submitted: d.day <= daysPassed ? d.submittedActual - prevSub : null,
      isToday: d.day === daysPassed,
      isPast: d.day < daysPassed,
      isFuture: d.day > daysPassed
    };
  });
  const spPercentage = softPullGoal.goal > 0 ? Math.round(softPullGoal.current / softPullGoal.goal * 100) : 0;
  const subPercentage = submittedGoal.goal > 0 ? Math.round(submittedGoal.current / submittedGoal.goal * 100) : 0;
  const spRemaining = Math.max(0, softPullGoal.goal - softPullGoal.current);
  const subRemaining = Math.max(0, submittedGoal.goal - submittedGoal.current);
  const selectedDayData = selectedDay ? dailyIncrements.find(d => d.day === selectedDay) : null;
  const selectedDayLoans = selectedDay ? dailyLoans.find(d => d.day === selectedDay)?.loans || [] : [];
  const handleDayClick = (day: number, hasData: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
    if (hasData) {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      // Position tooltip above the clicked day
      setTooltipPosition({
        top: rect.top - 10,
        left: rect.left + rect.width / 2
      });
      setSelectedDay(day);
    }
  };
  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setSelectedDay(null);
      }
    };
    if (selectedDay) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedDay]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  return <section className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-6" style={{
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
  }} aria-label="Daily breakdown" data-id="element-1">
      <div className="flex items-center justify-between mb-1" data-id="element-2">
        <h3 className="text-lg font-semibold text-white" data-id="element-3">Daily Breakdown</h3>
        <a href="https://yahoo.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors rounded-lg hover:bg-indigo-500/10" data-id="element-4">
          View Goals Dashboard
          <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-5" />
        </a>
      </div>
      <p className="text-sm text-slate-400 mb-6" data-id="element-6">
        Activity per day this period
      </p>

      {/* Goal Progress Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-slate-700/50" data-id="element-7">
        {/* Soft Pull Progress */}
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50" data-id="element-8">
          <div className="flex items-center justify-between mb-2" data-id="element-9">
            <span className="text-xs font-medium text-slate-400" data-id="element-10">
              Soft Pull Goal
            </span>
            <span className={`text-xs font-bold ${spPercentage >= 100 ? 'text-emerald-400' : 'text-indigo-400'}`} data-id="element-11">
              {spPercentage}%
            </span>
          </div>
          <div className="flex items-baseline gap-1 mb-3" data-id="element-12">
            <span className="text-2xl font-bold text-white" data-id="element-13">
              {softPullGoal.current}
            </span>
            <span className="text-sm text-slate-500" data-id="element-14">
              / {softPullGoal.goal}
            </span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2" data-id="element-15">
            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
            width: `${Math.min(spPercentage, 100)}%`,
            background: spPercentage >= 100 ? 'linear-gradient(90deg, #34d399, #10b981)' : 'linear-gradient(90deg, #6366f1, #a78bfa)'
          }} data-id="element-16" />
          </div>
          <p className="text-xs text-slate-500" data-id="element-17">
            {spRemaining > 0 ? `${spRemaining} remaining` : 'Goal completed!'}
          </p>
        </div>

        {/* Submitted Progress */}
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50" data-id="element-18">
          <div className="flex items-center justify-between mb-2" data-id="element-19">
            <span className="text-xs font-medium text-slate-400" data-id="element-20">
              Submitted Goal
            </span>
            <span className={`text-xs font-bold ${subPercentage >= 100 ? 'text-emerald-400' : 'text-cyan-400'}`} data-id="element-21">
              {subPercentage}%
            </span>
          </div>
          <div className="flex items-baseline gap-1 mb-3" data-id="element-22">
            <span className="text-2xl font-bold text-white" data-id="element-23">
              {submittedGoal.current}
            </span>
            <span className="text-sm text-slate-500" data-id="element-24">
              / {submittedGoal.goal}
            </span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2" data-id="element-25">
            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
            width: `${Math.min(subPercentage, 100)}%`,
            background: subPercentage >= 100 ? 'linear-gradient(90deg, #34d399, #10b981)' : 'linear-gradient(90deg, #06b6d4, #3b82f6)'
          }} data-id="element-26" />
          </div>
          <p className="text-xs text-slate-500" data-id="element-27">
            {subRemaining > 0 ? `${subRemaining} remaining` : 'Goal completed!'}
          </p>
        </div>
      </div>

      {/* Daily Grid */}
      <div className="grid grid-cols-7 gap-3" data-id="element-28">
        {dailyIncrements.map(d => {
        const hasData = d.softPull !== null;
        return <button key={d.day} onClick={e => handleDayClick(d.day, hasData, e)} disabled={!hasData} className={`rounded-xl p-3 text-center transition-all duration-300 ${d.isToday ? 'bg-indigo-500/10 ring-1 ring-indigo-500/40' : d.isPast ? 'bg-slate-700/50 hover:bg-slate-700/70 cursor-pointer' : 'bg-slate-800/50 opacity-40 cursor-not-allowed'}`} style={d.isToday ? {
          boxShadow: '0 0 15px rgba(129, 140, 248, 0.15)'
        } : undefined} data-id="element-29">
              <p className={`text-xs font-medium mb-2 ${d.isToday ? 'text-indigo-400' : 'text-slate-500'}`} data-id="element-30">
                {d.label}
              </p>
              {d.softPull !== null ? <>
                  <div className="mb-1" data-id="element-31">
                    <span className="text-lg font-bold text-indigo-400" data-id="element-32">
                      {d.softPull}
                    </span>
                    <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-33">SP</p>
                  </div>
                  <div data-id="element-34">
                    <span className="text-lg font-bold text-cyan-400" data-id="element-35">
                      {d.submitted}
                    </span>
                    <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-36">Sub</p>
                  </div>
                </> : <div className="py-3" data-id="element-37">
                  <span className="text-xs text-slate-600" data-id="element-38">—</span>
                </div>}
            </button>;
      })}
      </div>

      {/* Compact Tooltip Popover */}
      {selectedDay && selectedDayData && <div ref={tooltipRef} className="fixed z-50 bg-slate-800 rounded-xl border border-slate-700 shadow-2xl w-[340px] max-h-[400px] flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-200" style={{
      top: `${tooltipPosition.top}px`,
      left: `${tooltipPosition.left}px`,
      transform: 'translate(-50%, -100%)',
      boxShadow: '0 0 30px rgba(0,0,0,0.6)'
    }} data-id="element-39">
          {/* Tooltip Header */}
          <div className="px-4 py-3 border-b border-slate-700 flex-shrink-0" data-id="element-40">
            <p className="text-sm font-semibold text-white" data-id="element-41">
              {selectedDayData.label}
            </p>
            <p className="text-xs text-slate-400 mt-0.5" data-id="element-42">
              {selectedDayLoans.length} loan
              {selectedDayLoans.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Tooltip Content - Scrollable with hidden scrollbar */}
          <div className="overflow-y-auto flex-1 p-3" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-43">
            <style jsx data-id="element-44">{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {selectedDayLoans.length === 0 ? <p className="text-xs text-slate-500 text-center py-4" data-id="element-45">
                No activity
              </p> : <div className="space-y-2" data-id="element-46">
                {selectedDayLoans.map(loan => <div key={loan.id} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50 hover:border-slate-600/50 transition-colors" data-id="element-47">
                    <div className="flex items-start justify-between mb-1.5" data-id="element-48">
                      <span className="text-xs font-medium text-white" data-id="element-49">
                        {loan.borrowerName}
                      </span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${loan.type === 'softPull' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-cyan-500/10 text-cyan-400'}`} data-id="element-50">
                        {loan.type === 'softPull' ? 'SP' : 'Sub'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-51">
                      <span className="text-xs text-emerald-400 font-semibold" data-id="element-52">
                        {formatCurrency(loan.amount)}
                      </span>
                      <span className="text-[10px] text-slate-500" data-id="element-53">
                        {formatTime(loan.timestamp)}
                      </span>
                    </div>
                  </div>)}
              </div>}
          </div>

          {/* Tooltip Arrow */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full" style={{
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '8px solid #334155'
      }} data-id="element-54" />
        </div>}
    </section>;
}